import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { FacetProps } from './types';
import { useProductList } from '../../context';
import { RangeFacet as RangeFacetType } from '../../types';
const YearRangeFacet: React.FC<FacetProps> = ({ attribute }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { facetsConfiguration, refineRange } = useProductList();

  const facet = useMemo(() => facetsConfiguration[attribute] as RangeFacetType, [facetsConfiguration, attribute]);
  const [yearRange, setYearRange] = useState({
    min: facet?.minSelected ?? facet?.min ?? -Infinity,
    max: facet?.maxSelected ?? facet?.max ?? Infinity,
  });

  const refine = useCallback((value: [number, number]) => refineRange(attribute, value), [attribute, refineRange]);

  const applyRefinement = useCallback(
    (appliedRange: typeof yearRange) => {
      const min = Math.max(facet.min, appliedRange.min);
      const max = Math.min(facet.max, appliedRange.max);
      refine([min, max]);
    },
    [refine, facet],
  );

  const handleRangeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setYearRange((prevRange) => ({
      ...prevRange,
      [name]: +value,
    }));
  }, []);

  const handleRangeInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newValue = +value;

      setYearRange((prevRange) => ({
        ...prevRange,
        [name]:
          name === 'min'
            ? Math.max(facet.min, Math.min(newValue, prevRange.max))
            : Math.min(facet.max, Math.max(newValue, prevRange.min)),
      }));
    },
    [facet.min, facet.max],
  );

  const handleRangeSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      applyRefinement(yearRange);
    },
    [yearRange, applyRefinement],
  );
  return (
    <div>
      <form className="flex items-center gap-16" onSubmit={handleRangeSubmit}>
        <label
          htmlFor="min"
          className="flex w-85 items-center gap-4 border border-neutral-500 bg-white p-7"
          aria-label="min"
        >
          <input
            id="min"
            name="min"
            className="w-full border-none p-0 outline-none focus:border-none focus:outline-none"
            type="number"
            min={facet.min}
            max={facet.max}
            value={yearRange.min}
            placeholder={formatProductMessage({ id: 'min', defaultMessage: 'Min' })}
            onChange={handleRangeInputChange}
            onBlur={handleRangeInputBlur}
          />
        </label>

        <div className="w-16 border border-secondary-black" />

        <label
          htmlFor="max"
          className="flex w-85 items-center gap-4 border border-neutral-500 bg-white p-7"
          aria-label="max"
        >
          <input
            id="max"
            name="max"
            className="w-full border-none p-0 outline-none focus:border-none focus:outline-none"
            type="number"
            min={facet.min}
            max={facet.max}
            value={yearRange.max !== Infinity ? yearRange.max.toString() : ''}
            placeholder={formatProductMessage({ id: 'max', defaultMessage: 'Max' })}
            onChange={handleRangeInputChange}
            onBlur={handleRangeInputBlur}
          />
        </label>

        <button
          type="submit"
          className="rounded-sm bg-primary-black px-14 py-8 font-medium leading-[24px] text-white transition hover:bg-gray-500"
        >
          {formatProductMessage({ id: 'go', defaultMessage: 'Go' })}
        </button>
      </form>
    </div>
  );
};

export default YearRangeFacet;
